import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useProvisioningClient } from "@/lib/api";
import type {
  ClosureRequest,
  CreateClosureRequestInput,
  ResolveClosureRequestInput,
  ResolveClosureRequestResponse,
} from "@/types";

/**
 * T-42 project closure approval hooks (V054).
 *
 * The endpoints live on the Provisioning RestApi (infra/stacks/provisioning-infra.ts), so every
 * call goes through `useProvisioningClient` (VITE_PROVISIONING_API_URL), not the main API client.
 *  - POST /api/projects/{projectId}/closure-requests               — PM/SA (members) raise a request
 *  - GET  /api/projects/{projectId}/closure-requests?status=        — requests for one project
 *  - GET  /api/closure-requests?status=pending                     — leadership/admin queue
 *  - POST /api/projects/{projectId}/closure-requests/{id}/resolve  — leadership/admin approve/reject
 *
 * List endpoints return a bare array; `normalizeClosureRequests` also tolerates a `{ requests }`
 * envelope so a future wrapper does not silently empty the UI.
 */

export const closureRequestKeys = {
  all: ["closure-requests"] as const,
  project: (projectId: string) =>
    ["closure-requests", "project", projectId] as const,
  pending: ["closure-requests", "pending"] as const,
};

export function normalizeClosureRequests(
  body: ClosureRequest[] | { requests?: ClosureRequest[] } | null | undefined,
): ClosureRequest[] {
  if (Array.isArray(body)) return body;
  return body?.requests ?? [];
}

/** Pending closure request for one project (null when none). Drives the Settings card. */
export function useProjectClosureRequest(projectId: string, enabled = true) {
  const client = useProvisioningClient();
  return useQuery({
    queryKey: closureRequestKeys.project(projectId),
    queryFn: async () => {
      const response = await client.get<ClosureRequest[]>(
        `/api/projects/${encodeURIComponent(projectId)}/closure-requests?status=pending`,
        // A failed lookup degrades to "no pending request"; the card still works.
        { _suppressErrorToast: true },
      );
      return normalizeClosureRequests(response.data)[0] ?? null;
    },
    enabled: enabled && !!projectId,
  });
}

/** Leadership/admin cross-project queue of pending closure requests (Dashboard section). */
export function usePendingClosureRequests(enabled = true) {
  const client = useProvisioningClient();
  return useQuery({
    queryKey: closureRequestKeys.pending,
    queryFn: async () => {
      const response = await client.get<ClosureRequest[]>(
        "/api/closure-requests?status=pending",
      );
      return normalizeClosureRequests(response.data);
    },
    enabled,
  });
}

/** PM/SA raise a closure request. */
export function useCreateClosureRequest(projectId: string) {
  const client = useProvisioningClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: CreateClosureRequestInput) => {
      const response = await client.post<ClosureRequest>(
        `/api/projects/${encodeURIComponent(projectId)}/closure-requests`,
        data,
      );
      return response.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: closureRequestKeys.project(projectId),
      });
      void queryClient.invalidateQueries({
        queryKey: closureRequestKeys.pending,
      });
    },
  });
}

/** Leadership/admin approve or reject. Approval closes the project server-side. */
export function useResolveClosureRequest() {
  const client = useProvisioningClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      request,
      input,
    }: {
      request: Pick<ClosureRequest, "id" | "project_id">;
      input: ResolveClosureRequestInput;
    }) => {
      const response = await client.post<ResolveClosureRequestResponse>(
        `/api/projects/${encodeURIComponent(request.project_id)}/closure-requests/${encodeURIComponent(request.id)}/resolve`,
        input,
      );
      return response.data;
    },
    onSuccess: (_data, { request }) => {
      void queryClient.invalidateQueries({
        queryKey: closureRequestKeys.pending,
      });
      void queryClient.invalidateQueries({
        queryKey: closureRequestKeys.project(request.project_id),
      });
      // Approving closes the project — refresh its detail + the lists.
      void queryClient.invalidateQueries({
        queryKey: ["project", request.project_id],
      });
      void queryClient.invalidateQueries({ queryKey: ["projects"] });
    },
  });
}
